import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { browserPushSupport, syncBrowserPushToken } from '../services/browserPush';

const SNOOZE_KEY = 'coachhub_push_prompt_snoozed_until';

export default function BrowserPushManager() {
  const { user, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (loading || !user) { setVisible(false); return; }
    const support = browserPushSupport();
    if (!support.supported) return;
    if (support.permission === 'granted') {
      syncBrowserPushToken().catch((error) => console.warn('[Push] Token sync failed:', error));
      return;
    }
    if (support.permission === 'denied') { setBlocked(true); setVisible(true); return; }
    const snoozedUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() >= snoozedUntil) setVisible(true);
  }, [loading, user]);

  useEffect(() => {
    const handler = (event) => toast(event.detail?.title || 'New notification', { description: event.detail?.body });
    window.addEventListener('coachhub:push-message', handler);
    return () => window.removeEventListener('coachhub:push-message', handler);
  }, []);

  if (!visible || !user) return null;

  const dismiss = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  const enable = async () => {
    setEnabling(true);
    try {
      const result = await syncBrowserPushToken({ requestPermission: true });
      if (result.registered) {
        setVisible(false);
        setBlocked(false);
        toast.success('Browser notifications enabled');
      } else if (result.permission === 'denied') {
        setBlocked(true);
        toast.error('Notifications are blocked in browser settings');
      }
    } catch (error) {
      console.error('[Push] Enable failed:', error);
      toast.error('Browser notification setup failed. Please try again.');
    } finally { setEnabling(false); }
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-5 sm:w-[390px] z-[10000] rounded-2xl border border-indigo-200 bg-white p-4 shadow-2xl" role="status">
      <button onClick={dismiss} className="absolute right-3 top-3 rounded-lg p-1 text-slate-400 hover:bg-slate-100" aria-label="Dismiss notification prompt"><X className="h-4 w-4" /></button>
      <div className="flex items-start gap-3 pr-6">
        <div className={'rounded-xl p-2.5 ' + (blocked ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600')}>
          {blocked ? <BellOff className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900">{blocked ? 'Browser notifications blocked' : 'Enable browser notifications'}</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            {blocked ? 'Browser site settings me Notifications ko Allow karein, phir page reload karein.' : 'Attendance, fees aur important updates browser par turant paayen.'}
          </p>
          {!blocked && <button onClick={enable} disabled={enabling} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
            {enabling && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Enable notifications
          </button>}
        </div>
      </div>
    </div>
  );
}